import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Phone, CheckCircle, XCircle, Lightbulb, 
  MessageSquare, Target, TrendingUp, Award, AlertTriangle,
  RefreshCw, Calendar, Baby, Star, BookOpen, Brain,
  ThumbsUp, ThumbsDown, Zap, Quote
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverCallAnalysis = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadAnalysis();
  }, [navigate]);

  const loadAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/analysis/observer/call-analysis?token=${token}`
      );
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error loading analysis:', error);
      toast({ title: "Error", description: "Failed to load call analysis", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'text-green-600 bg-green-50';
    if (score >= 5) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getScoreBar = (score) => {
    const percentage = (score / 10) * 100;
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${
            score >= 7 ? 'bg-green-500' : score >= 5 ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="observer-call-analysis">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/observer/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Brain className="w-6 h-6 text-purple-600" />
                  Call Analysis
                </h1>
                <p className="text-gray-600">AI-powered feedback on your latest session</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadAnalysis} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* No Analysis State */}
          {!analysis?.has_analysis && (
            <Card className="text-center py-12">
              <CardContent>
                <Brain className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Analysis Yet</h3>
                <p className="text-gray-500">{analysis?.message || "Complete some sessions to see AI-powered analysis"}</p>
              </CardContent>
            </Card>
          )}

          {/* Analysis Content */}
          {analysis?.has_analysis && (
            <>
              {/* Session Info Header */}
              <Card className="mb-6 bg-gradient-to-r from-purple-500 to-violet-600 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                        <Baby className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">{analysis.child_name}</h2>
                        <p className="text-purple-100">Session #{analysis.call_number} • {analysis.date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-purple-100 text-sm">Latest Analysis</p>
                      <p className="text-2xl font-bold">🎯 Ready</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What Worked / What to Improve */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* What Worked */}
                <Card className="border-green-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <ThumbsUp className="w-5 h-5" />
                      What Worked Well
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analysis.what_worked?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{item}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Learning Points */}
                <Card className="border-amber-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <Lightbulb className="w-5 h-5" />
                      Learning Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analysis.learning_points?.length > 0 ? (
                        analysis.learning_points.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700 text-sm">{item}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">Great job! No specific areas to improve this session.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Session Summary */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    Session Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{analysis.summary}</p>
                </CardContent>
              </Card>

              {/* Key Quotes */}
              {analysis.key_quotes?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Quote className="w-5 h-5 text-violet-600" />
                      Key Quotes to Remember
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {analysis.key_quotes.map((quote, idx) => (
                        <div key={idx} className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                          <p className="text-violet-800 italic">"{quote}"</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Performance Scores */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    Performance Scores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {Object.entries(analysis.performance_scores || {}).map(([key, value]) => (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 capitalize">
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span className={`text-sm font-semibold px-2 py-0.5 rounded ${getScoreColor(value)}`}>
                            {value}/10
                          </span>
                        </div>
                        {getScoreBar(value)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* AI Coaching Tips */}
              <Card className="mb-6 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700">
                    <Zap className="w-5 h-5" />
                    AI Coaching Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analysis.coaching_tips?.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-purple-100">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-purple-600 font-bold text-sm">{idx + 1}</span>
                        </div>
                        <p className="text-gray-700">{tip}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/observer/prep-notes')}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  View Prep Notes for Next Call
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/observer/sessions')}
                  className="flex-1"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  View All Sessions
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverCallAnalysis;
